import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { isClinicModuleEnabled, readActivatedModules } from '@/lib/clinicModules';
import {
  PERMISSION_MATRIX_ACTIONS,
  PERMISSION_RESOURCE_GROUPS,
  DEPRECATED_MATRIX_RESOURCES,
  actionsForResource,
  applyClinicAdminLockout,
  isClinicAdminLockedGrant,
  type PermissionAction,
  type PermissionMap,
  type PermissionResource,
} from '@/lib/permissions';
import { clinicRoleDisplayName } from '@/lib/clinicRoleLabel';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  useClinicRoles,
  useCreateClinicRole,
  useDeleteClinicRole,
  useSetClinicRolePermissions,
  useUpdateClinicRole,
  type ClinicRoleRow,
} from '@/hooks/useClinicRoles';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { TYPE } from '@/lib/typography';
import {
  SaveBar,
  SectionCard,
  SettingsEmpty,
  SettingsHint,
  SettingsLoading,
} from './settingsChrome';
import { cn } from '@/utils/cn';

/** Permission resources that only appear when their clinic module is enabled. */
const MODULE_GATED_RESOURCES: Partial<Record<PermissionResource, string>> = {
  referrals: 'referrals',
};

/** Hidden from the roles matrix (orphaned or clinic-type specific). */
const HIDDEN_RESOURCES = new Set<PermissionResource>([...DEPRECATED_MATRIX_RESOURCES]);

/** Shown indented under the Korrespondance group heading. */
const KORRESPONDANCE_SUB_RESOURCES = new Set<PermissionResource>([
  'medcom_xdis',
  'medcom_ggop',
  'medcom_sup',
]);

export function ClinicRoles() {
  const { t } = useTranslation();
  const roleLabel = (role: { name: string; system_key: string | null }) =>
    clinicRoleDisplayName(role, (key) => t(key));
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const { data: clinic } = useClinic(activeClinicId);
  const activatedModules = readActivatedModules(clinic?.activated_modules);

  const visibleResourceGroups = useMemo(
    () =>
      PERMISSION_RESOURCE_GROUPS.map((group) => ({
        ...group,
        resources: group.resources.filter((resource) => {
          if (HIDDEN_RESOURCES.has(resource)) return false;
          const moduleKey = MODULE_GATED_RESOURCES[resource];
          if (!moduleKey) return true;
          return isClinicModuleEnabled(activatedModules, moduleKey);
        }),
      })).filter((group) => group.resources.length > 0),
    [activatedModules],
  );

  const canView = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_roles', 'view'),
  );
  const canEdit = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_roles', 'update'),
  );

  const { data: roles, isLoading, error } = useClinicRoles(canView ? activeClinicId : null);
  const createRole = useCreateClinicRole(activeClinicId);
  const updateRole = useUpdateClinicRole(activeClinicId);
  const deleteRole = useDeleteClinicRole(activeClinicId);
  const setPermissions = useSetClinicRolePermissions(activeClinicId);

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PermissionMap>({});
  const [newName, setNewName] = useState('');
  const [dirty, setDirty] = useState(false);

  const selected = useMemo(
    () => roles?.find((r) => r.id === selectedRoleId) ?? roles?.[0] ?? null,
    [roles, selectedRoleId],
  );

  useEffect(() => {
    if (!roles?.length) {
      setSelectedRoleId(null);
      return;
    }
    if (!selectedRoleId || !roles.some((r) => r.id === selectedRoleId)) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selected) {
      setDraft({});
      setDirty(false);
      return;
    }
    setDraft(structuredClone(selected.permissions));
    setDirty(false);
  }, [selected?.id, selected?.updated_at]);

  if (!activeClinicId) {
    return <ErrorAlert message={t('settings.no_clinic')} />;
  }

  if (!canView) {
    return <ErrorAlert message={t('roles.forbidden')} />;
  }

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error) return <ErrorAlert message={t('roles.load_error')} />;

  const matrixColumns: PermissionAction[] = [...PERMISSION_MATRIX_ACTIONS, 'finalize', 'amend'];

  function toggle(resource: PermissionResource, action: PermissionAction) {
    if (!canEdit) return;
    if (selected?.system_key === 'clinic_admin' && isClinicAdminLockedGrant(resource, action)) {
      return;
    }
    setDraft((prev) => {
      const current = new Set(prev[resource] ?? []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      return { ...prev, [resource]: Array.from(current) as PermissionAction[] };
    });
    setDirty(true);
  }

  async function onSaveMatrix() {
    if (!selected || !canEdit) return;
    const payload =
      selected.system_key === 'clinic_admin' ? applyClinicAdminLockout(draft) : draft;
    try {
      await setPermissions.mutateAsync({ roleId: selected.id, permissions: payload });
      setDirty(false);
      toast.success(t('roles.saved_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  async function onCreateRole() {
    const name = newName.trim();
    if (!name || !canEdit) return;
    try {
      const created = (await createRole.mutateAsync({ name })) as { id?: string } | null;
      setNewName('');
      if (created?.id) setSelectedRoleId(created.id);
      toast.success(t('roles.created_toast'));
    } catch {
      toast.error(t('roles.create_error_toast'));
    }
  }

  async function onDeleteRole(role: ClinicRoleRow) {
    if (!canEdit || role.is_system) return;
    if (!window.confirm(t('roles.delete_confirm', { name: roleLabel(role) }))) return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success(t('roles.deleted_toast'));
    } catch {
      toast.error(t('roles.delete_error_toast'));
    }
  }

  async function onRename(role: ClinicRoleRow, name: string) {
    const next = name.trim();
    if (!canEdit || !next || next === role.name || next === roleLabel(role)) return;
    try {
      await updateRole.mutateAsync({ roleId: role.id, name: next });
      toast.success(t('roles.renamed_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  async function onTogglePractitioner(role: ClinicRoleRow, isPractitioner: boolean) {
    if (!canEdit) return;
    try {
      await updateRole.mutateAsync({ roleId: role.id, isPractitioner });
      toast.success(t('roles.practitioner_saved_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  async function onToggleJournalPatientConfirm(role: ClinicRoleRow, required: boolean) {
    if (!canEdit) return;
    try {
      await updateRole.mutateAsync({
        roleId: role.id,
        requireJournalPatientConfirm: required,
      });
      toast.success(t('roles.require_journal_patient_confirm_saved_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <SectionCard title={t('roles.title')} subtitle={t('roles.subtitle')} readOnly={!canEdit}>
        <div className="flex min-w-0 flex-col gap-s6 nav:flex-row">
          <aside className="w-full shrink-0 space-y-s3 nav:w-60">
            <ul className="space-y-s1">
              {(roles ?? []).map((role) => (
                <li key={role.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedRoleId(role.id)}
                    className={cn(
                      TYPE.control,
                      'flex min-h-10 w-full items-center justify-between rounded-ctl px-s3 py-s2 text-left transition-colors',
                      selected?.id === role.id
                        ? 'bg-ds-teal-tint font-semibold text-ds-teal'
                        : 'text-ds-text hover:bg-ds-neutral-tint',
                    )}
                  >
                    <span className="min-w-0 truncate font-medium">
                      {roleLabel(role)}
                      {role.is_system ? (
                        <span className={cn(TYPE.small, 'ml-1 font-normal text-ds-muted')}>
                          ({t('roles.system_badge')})
                        </span>
                      ) : null}
                      {role.is_practitioner ? (
                        <span className={cn(TYPE.small, 'ml-1 font-normal text-ds-teal')}>
                          {t('roles.practitioner_badge')}
                        </span>
                      ) : null}
                    </span>
                    <span className={cn(TYPE.small, 'ml-2 shrink-0 text-ds-muted')}>
                      {role.member_count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {canEdit ? (
              <div className="flex gap-s2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('roles.new_role_placeholder')}
                  className="min-w-0 flex-1"
                />
                <Button
                  type="button"
                  size="icon"
                  onClick={() => void onCreateRole()}
                  disabled={!newName.trim() || createRole.isPending}
                  aria-label={t('roles.new_role_placeholder')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
          </aside>

          <div className="min-w-0 flex-1 space-y-s4">
            {!selected ? (
              <SettingsEmpty title={t('roles.empty')} icon={null} />
            ) : (
              <form
                className="flex min-w-0 flex-col gap-s4"
                onSubmit={(e) => {
                  e.preventDefault();
                  void onSaveMatrix();
                }}
              >
                <div className="flex min-w-0 flex-col gap-s3">
                  <div className="min-w-0">
                    {canEdit ? (
                      <Input
                        defaultValue={roleLabel(selected)}
                        key={`${selected.id}:${roleLabel(selected)}`}
                        onBlur={(e) => void onRename(selected, e.target.value)}
                        aria-label={t('roles.rename_label')}
                      />
                    ) : (
                      <h2 className={cn(TYPE.heading, 'text-ds-text')}>{roleLabel(selected)}</h2>
                    )}
                    <SettingsHint className="mt-s2">
                      {selected.system_key === 'clinic_admin'
                        ? t('roles.clinic_admin_template_hint')
                        : selected.is_system
                          ? t('roles.employee_template_hint')
                          : t('roles.custom_role_hint')}
                    </SettingsHint>
                    <div className="mt-s3 space-y-s3">
                      <Checkbox
                        checked={selected.is_practitioner}
                        disabled={!canEdit || updateRole.isPending}
                        onChange={(e) => void onTogglePractitioner(selected, e.target.checked)}
                        label={t('roles.is_practitioner')}
                        hint={t('roles.is_practitioner_hint')}
                      />
                      <Checkbox
                        checked={selected.require_journal_patient_confirm}
                        disabled={!canEdit || updateRole.isPending}
                        onChange={(e) =>
                          void onToggleJournalPatientConfirm(selected, e.target.checked)
                        }
                        label={t('roles.require_journal_patient_confirm')}
                        hint={t('roles.require_journal_patient_confirm_hint')}
                      />
                    </div>
                  </div>
                  {canEdit && !selected.is_system ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full nav:w-auto"
                      onClick={() => void onDeleteRole(selected)}
                      disabled={deleteRole.isPending || selected.member_count > 0}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('roles.delete')}
                    </Button>
                  ) : null}
                </div>

                <div className="hidden min-w-0 nav:block">
                  <table className="w-full min-w-0 border-collapse text-sm">
                    <thead>
                      <tr>
                        <th className={cn(TYPE.meta, 'border-b border-ds-line px-s3 pb-s2 text-left text-ds-muted')}>
                          {t('roles.col_resource')}
                        </th>
                        {matrixColumns.map((action) => (
                          <th
                            key={action}
                            className={cn(TYPE.meta, 'border-b border-ds-line px-s3 pb-s2 text-center text-ds-muted')}
                          >
                            {t(`roles.action_${action}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleResourceGroups.map((group) => (
                        <Fragment key={group.groupKey}>
                          <tr>
                            <td
                              colSpan={1 + matrixColumns.length}
                              className={cn(TYPE.caption, 'bg-ds-sunk px-s3 py-s2 font-semibold uppercase tracking-wide text-ds-muted')}
                            >
                              {t(`roles.group_${group.groupKey}`)}
                            </td>
                          </tr>
                          {group.resources.map((resource) => (
                            <tr key={resource}>
                              <td className={cn(TYPE.body, 'border-b border-ds-line px-s3 py-s3 text-ds-text')}>
                                <span className={cn(KORRESPONDANCE_SUB_RESOURCES.has(resource) && 'pl-s4')}>
                                  {t(`roles.resource_${resource}`)}
                                </span>
                                {t(`roles.resource_${resource}_hint`, '') ? (
                                  <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>
                                    {t(`roles.resource_${resource}_hint`)}
                                  </span>
                                ) : null}
                              </td>
                              {matrixColumns.map((action) => {
                                const allowed = actionsForResource(resource).includes(action);
                                if (!allowed) {
                                  return (
                                    <td
                                      key={action}
                                      className="border-b border-ds-line px-s3 py-s3 text-center text-ds-muted"
                                    >
                                      —
                                    </td>
                                  );
                                }
                                const locked =
                                  selected.system_key === 'clinic_admin' &&
                                  isClinicAdminLockedGrant(resource, action);
                                const checked =
                                  locked || (draft[resource] ?? []).includes(action);
                                return (
                                  <td key={action} className="border-b border-ds-line px-s3 py-s3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={!canEdit || locked}
                                      onChange={() => toggle(resource, action)}
                                      className="h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring disabled:opacity-50"
                                      aria-label={`${resource} ${action}`}
                                      title={locked ? t('roles.admin_lock_hint') : undefined}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex min-w-0 flex-col gap-s4 nav:hidden">
                  {visibleResourceGroups.map((group) => (
                    <div key={group.groupKey} className="min-w-0">
                      <h3 className={cn(TYPE.caption, 'mb-s2 font-semibold uppercase tracking-wide text-ds-muted')}>
                        {t(`roles.group_${group.groupKey}`)}
                      </h3>
                      <div className="flex flex-col gap-s3">
                        {group.resources.map((resource) => (
                          <div
                            key={resource}
                            className="rounded-ctl border border-ds-line bg-ds-card p-s3"
                          >
                            <p className={cn(TYPE.body, 'text-ds-text', KORRESPONDANCE_SUB_RESOURCES.has(resource) && 'pl-s2')}>
                              {t(`roles.resource_${resource}`)}
                            </p>
                            {t(`roles.resource_${resource}_hint`, '') ? (
                              <SettingsHint className="mt-s1">
                                {t(`roles.resource_${resource}_hint`)}
                              </SettingsHint>
                            ) : null}
                            <div className="mt-s3 grid grid-cols-2 gap-s2">
                              {matrixColumns.map((action) => {
                                const allowed = actionsForResource(resource).includes(action);
                                if (!allowed) return null;
                                const locked =
                                  selected.system_key === 'clinic_admin' &&
                                  isClinicAdminLockedGrant(resource, action);
                                const checked =
                                  locked || (draft[resource] ?? []).includes(action);
                                return (
                                  <Checkbox
                                    key={action}
                                    checked={checked}
                                    disabled={!canEdit || locked}
                                    onChange={() => toggle(resource, action)}
                                    label={t(`roles.action_${action}`)}
                                    title={locked ? t('roles.admin_lock_hint') : undefined}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <SaveBar
                  canEdit={canEdit}
                  pending={setPermissions.isPending}
                  disabled={!dirty}
                  onCancel={
                    selected
                      ? () => {
                          setDraft(structuredClone(selected.permissions));
                          setDirty(false);
                        }
                      : undefined
                  }
                />
              </form>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
